import { getEmailCampaignTopLinkLabel, normalizeEmailLinkUrl } from "./emailCampaignTopLinks";
function hrefMatchesTarget(href: string, targetUrl: string): boolean {
  const normalizedHref = normalizeEmailLinkUrl(href);
  const normalizedTarget = normalizeEmailLinkUrl(targetUrl);

  if (normalizedHref.length === 0 || normalizedTarget.length === 0) {
    return false;
  }

  if (normalizedHref === normalizedTarget) {
    return true;
  }

  return normalizedHref.includes(normalizedTarget) || normalizedTarget.includes(normalizedHref);
}

function isVisibleAnchor(anchor: HTMLAnchorElement): boolean {
  const rect = anchor.getBoundingClientRect();
  if (rect.width < 4 || rect.height < 4) {
    return false;
  }

  const view = anchor.ownerDocument.defaultView;
  const style = view?.getComputedStyle(anchor);
  if (style == null) {
    return true;
  }

  return style.display !== "none" && style.visibility !== "hidden" && Number(style.opacity) > 0;
}

function scoreAnchorMatch(anchor: HTMLAnchorElement, targetUrl: string, label?: string | null): number {
  const href = anchor.getAttribute("href") ?? "";
  if (!hrefMatchesTarget(href, targetUrl)) {
    return -1;
  }

  let score = 0;
  const normalizedHref = normalizeEmailLinkUrl(href);
  const normalizedTarget = normalizeEmailLinkUrl(targetUrl);
  const text = anchor.textContent?.trim() ?? "";
  const normalizedLabel = label?.trim().toLowerCase() ?? "";

  if (normalizedLabel.length > 0 && text.toLowerCase() === normalizedLabel) {
    score += 2000;
  } else if (normalizedLabel.length > 0 && text.toLowerCase().includes(normalizedLabel)) {
    score += 1200;
  }

  if (normalizedHref === normalizedTarget) {
    score += 800;
  } else {
    score += 200;
  }

  const rect = anchor.getBoundingClientRect();
  score += Math.min(rect.width * rect.height, 10000) / 10;

  if (text.length > 0) {
    score += 100;
  }

  if (isVisibleAnchor(anchor)) {
    score += 500;
  } else {
    score -= 1000;
  }

  return score;
}

export function findEmailPreviewLinkTarget(
  doc: Document,
  targetUrl: string,
  label?: string | null,
): HTMLElement | null {
  const anchors: HTMLAnchorElement[] = Array.from(doc.querySelectorAll("a[href]"));

  let bestAnchor: HTMLAnchorElement | null = null;
  let bestScore = -1;

  for (const anchor of anchors) {
    const score = scoreAnchorMatch(anchor, targetUrl, label);
    if (score > bestScore) {
      bestScore = score;
      bestAnchor = anchor;
    }
  }

  if (bestAnchor != null && bestScore >= 0) {
    return bestAnchor;
  }

  const fallbackLabel = label?.trim() || getEmailCampaignTopLinkLabel({ url: targetUrl, count: 0 });
  const normalizedFallback = fallbackLabel.toLowerCase();

  return (
    anchors.find(anchor => {
      if (!isVisibleAnchor(anchor)) {
        return false;
      }

      const text = anchor.textContent?.trim().toLowerCase() ?? "";
      return text === normalizedFallback || text.includes(normalizedFallback);
    }) ?? null
  );
}
